import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ScrollText, ChevronLeft, ChevronRight, User, Flag, Shield,
  LogIn, LogOut, Ban, AlertTriangle, Eye, UserCheck, Settings, Bell
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30'
      });
      
      if (actionFilter !== 'all') params.append('action', actionFilter);

      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'admin_login': return <LogIn className="w-4 h-4 text-green-500" />;
      case 'admin_logout': return <LogOut className="w-4 h-4 text-gray-500" />;
      case 'user_viewed': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'user_suspended': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'user_banned': return <Ban className="w-4 h-4 text-red-500" />;
      case 'user_unsuspended':
      case 'user_unbanned': return <UserCheck className="w-4 h-4 text-green-500" />;
      case 'user_warned': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'user_deleted': return <Ban className="w-4 h-4 text-red-600" />;
      case 'user_verified': return <Shield className="w-4 h-4 text-[#08D9D6]" />;
      case 'report_viewed': return <Flag className="w-4 h-4 text-blue-500" />;
      case 'report_resolved': return <Flag className="w-4 h-4 text-green-500" />;
      case 'report_escalated': return <Flag className="w-4 h-4 text-red-500" />;
      case 'report_dismissed': return <Flag className="w-4 h-4 text-gray-500" />;
      case 'settings_changed': return <Settings className="w-4 h-4 text-purple-500" />;
      case 'broadcast_sent': return <Bell className="w-4 h-4 text-[#FF2E63]" />;
      default: return <ScrollText className="w-4 h-4 text-[#A1A1AA]" />;
    }
  };

  const getActionColor = (action) => {
    if (action.includes('banned') || action.includes('deleted')) return 'text-red-500';
    if (action.includes('suspended') || action.includes('warned')) return 'text-yellow-500';
    if (action.includes('verified') || action.includes('resolved') || action.includes('login')) return 'text-green-500';
    if (action.includes('viewed')) return 'text-blue-500';
    return 'text-[#A1A1AA]';
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const totalPages = Math.ceil(total / 30);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-[#A1A1AA]">{total} total entries</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-56 bg-[#111] border-white/10">
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="admin_login">Admin Login</SelectItem>
            <SelectItem value="user_viewed">User Viewed</SelectItem>
            <SelectItem value="user_suspended">User Suspended</SelectItem>
            <SelectItem value="user_banned">User Banned</SelectItem>
            <SelectItem value="user_warned">User Warned</SelectItem>
            <SelectItem value="user_verified">User Verified</SelectItem>
            <SelectItem value="report_resolved">Report Resolved</SelectItem>
            <SelectItem value="report_escalated">Report Escalated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <ScrollText className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No audit logs found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Timestamp</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Admin</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Action</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Target</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-white/5">
                  <td className="p-4 text-[#A1A1AA] text-sm whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {log.admin_username?.[0]?.toUpperCase() || 'A'}
                        </span>
                      </div>
                      <span className="text-white font-medium">{log.admin_username}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {getActionIcon(log.action)}
                      <span className={`font-medium ${getActionColor(log.action)}`}>
                        {formatAction(log.action)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-[#A1A1AA]">
                    <span className="px-2 py-1 bg-white/5 rounded text-xs">
                      {log.target_type}: {log.target_id?.slice(0, 8)}...
                    </span>
                  </td>
                  <td className="p-4 text-[#52525B] text-sm">
                    {log.details && Object.keys(log.details).length > 0 ? (
                      <span className="truncate max-w-xs block">
                        {JSON.stringify(log.details).slice(0, 50)}...
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <p className="text-sm text-[#A1A1AA]">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-[#A1A1AA]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="text-[#A1A1AA]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogs;
